'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { Language, getTranslation, isRTL } from '@/lib/i18n';
import { translationBackupService } from '@/lib/translation-backup.service';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang && ['en', 'ar', 'fa', 'ru'].includes(savedLang)) {
      setLanguageState(savedLang);
    }

    // Auto-restore missing translations on mount
    const restoreResult = translationBackupService.autoRestore();
    if (restoreResult.restored && restoreResult.restoredCount > 0) {
      console.log(`[Language Context] Restored ${restoreResult.restoredCount} translations`);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL(language) ? 'rtl' : 'ltr';
  }, [language]);

  const setLanguage = (lang: Language) => {
    // Removed automatic backup on language change to prevent localStorage quota issues
    // Backups are still created via auto-backup schedule and manual triggers
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string) => getTranslation(key, language);

  return (
    <LanguageContext.Provider data-component-id="src/contexts/LanguageContext.tsx:47:4" data-component-path="src/contexts/LanguageContext.tsx" data-component-line="47" data-component-end-line="49" data-component-file="LanguageContext.tsx" data-component-name="LanguageContext.Provider" data-component-content="%7B%22elementName%22%3A%22LanguageContext.Provider%22%7D" value={{ language, setLanguage, t, isRTL: isRTL(language) }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};